import React, { useEffect, useRef } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { NavLink, useNavigate } from 'react-router-dom';

import { bemCssModules, getTestId } from '@utils';
import { useModal } from '@utils/hooks';

import { globalActions, loginActions, useStore } from '@modules';

import Logo from '@components/Logo';

import Menu from './Menu';
import MenuButton from './MenuButton';
import SchemeSwitcher from './SchemeSwitcher';
import BackIcon from './icon_back.svg';
import useMenuLinks from './useMenuLinks';

import style from './Header.scss';

const cn = bemCssModules(style, 'Header');
const ti = getTestId('Header');

export type THeader = {
  backButton?: boolean | (() => void);
  closeButton?: boolean | (() => void);
  isHideLinks?: boolean;
  onLogoClick?: () => void;
  isBlockedPage?: boolean;
};

const Header: CustomFC<THeader> = props => {
  const {
    className,
    backButton,
    closeButton,
    onLogoClick,
    isHideLinks,
    isBlockedPage = false,
  } = props;

  const { t } = useTranslation();
  const [{ login, global }, dispatch] = useStore();
  const { protect } = global;
  const { isLoggedIn } = login;
  const headerRef = useRef<HTMLDivElement>(null);
  const [menuIsOpen, setMenuIsOpen] = useModal([headerRef], true);
  const navigate = useNavigate();
  const menuLinks = useMenuLinks();
  const { state, pathname } = useLocation();

  const logout = () => dispatch(loginActions.logout());
  const toggleMenu = () => setMenuIsOpen(!menuIsOpen);
  const onBack = () => {
    if (typeof backButton === 'function') backButton();
    else navigate(-1);
  };
  const onClose = () => {
    if (typeof closeButton === 'function') closeButton();
    else navigate(-1);
  };
  const handleLogoClick = () => {
    if (!isLoggedIn) {
      window.location.href = CONST.website;
    }

    if (typeof onLogoClick === 'function') onLogoClick();
    else navigate(CONST.nav.root);
  };

  useEffect(() => {
    if (protect && pathname !== CONST.nav[protect]) {
      dispatch(
        globalActions.redirectTo({
          to: protect,
          state: { ...state, from: pathname, nextPage: pathname, flow: 'verify2fa' },
        })
      );
      dispatch(globalActions.setProtect(undefined));
    }
  }, [protect]);

  const handleClick = () => dispatch(globalActions.clickMenuItem());

  return (
    <header ref={headerRef} className={cn({ blur: !menuIsOpen }, [className])} data-testid={ti()}>
      <div className={cn('wrapper')}>
        {backButton ? (
          <button
            className={cn('back')}
            data-testid={ti('back-button')}
            type="button"
            onClick={onBack}
          >
            <BackIcon />
          </button>
        ) : !isHideLinks ? (
          <button type="button" className={cn('logo')} onClick={handleLogoClick}>
            <Logo />
          </button>
        ) : (
          <div />
        )}
        {!closeButton ? (
          <>
            {isLoggedIn && !isBlockedPage && !isHideLinks && (
              <nav className={cn('nav')}>
                {menuLinks.map(({ key, url }) => (
                  <NavLink
                    key={key}
                    onClick={handleClick}
                    className={({ isActive }) => cn('link', { isActive }, ['link'])}
                    to={url}
                    data-testid={ti(key)}
                  >
                    {t(`header.${key}`)}
                  </NavLink>
                ))}
                <button className={cn('link', ['link'])} type="button" onClick={logout}>
                  {t('header.log_out')}
                </button>
              </nav>
            )}
            {!isBlockedPage && (
              <div className={cn('icon-buttons')}>
                <SchemeSwitcher className={cn('scheme-switcher', { menuIsOpen })} />
                <MenuButton
                  className={cn('menu-button', { isLoggedIn, isHideLinks })}
                  isOpen={menuIsOpen}
                  onClick={toggleMenu}
                />
              </div>
            )}
          </>
        ) : (
          <MenuButton className={cn('menu-button')} isOpen onClick={onClose} />
        )}
      </div>
      <Menu className={cn('menu')} isOpen={menuIsOpen} />
    </header>
  );
};

export default Header;
